<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/Ease_of_Editing';
    let nextLink= '/Documentation/Defining_an_Editor/Number_Projections';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="boolean-projections-1"
  bind:intersecting={visibleById["boolean-projections-1"]}
  onIntersectChange={(visible) => setVisible("boolean-projections-1", visible)}
>Boolean Projections</SectionComponent>
<p>Freon offers a number of different ways to display a boolean value, ranging from textual to
visual controls, like a switch. Each option is described in the following.</p>
<p>Note that you can set a default for the display of a boolean value in the <code>global</code> section of the
default editor. See <a
  href="/Documentation/Defining_an_Editor/Global_Projections"
>Global Projections</a>.</p>
<SectionComponent tag="h2" 
  id="textual---two-keywords-2"
  bind:intersecting={visibleById["textual---two-keywords-2"]}
  onIntersectChange={(visible) => setVisible("textual---two-keywords-2", visible)}
>Textual - Two Keywords</SectionComponent>
<p>The strings displayed for boolean values — normally <code>true</code> and <code>false</code> — can be customized.
In the projection two strings are added,
the first is the text that represents the true-value, the second represents the false-value.</p>
<p>In the next example, the concept <code>InsurancePart</code> has a simple property of type boolean
called <code>isApproved</code>. In the projection for <code>isApproved</code> the strings <code>JA</code> and <code>NEE</code> will be shown for,
respectively, true and false.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L41-L49

// An InsurancePart defines a single aspect of an InsuranceProduct together
// with the statistical risk of the event happening, and the maximum payout
// in case the insured event happens.
concept InsurancePart &#123;
    name: identifier;                       // internal name
    isApproved: boolean = false;            // indication of approval status
    statisticalRisk: PercentageLiteral;     // the statistical risk known for this event
    maximumPayOut: EuroLiteral;             // maximum payout in case the insured event happens
&#125;</code></pre>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-default.edit#L28-L35


InsurancePart&#123;
[
    Insurance Part $&#123;self.name&#125;
        risk assessment: $&#123;self.statisticalRisk&#125;
        maximum payout: $&#123;self.maximumPayOut&#125;
        is approved: $&#123;self.isApproved [YES | NO]&#125;
]</code></pre>
<Figure
imageName={'documentation/Documentation-Boolean-Projections-screenshot1.png'}
caption={'Displaying different strings for boolean values'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="textual---one-keyword-3"
  bind:intersecting={visibleById["textual---one-keyword-3"]}
  onIntersectChange={(visible) => setVisible("textual---one-keyword-3", visible)}
>Textual - One Keyword</SectionComponent>
<p>It is not necessary to always provide two textual representations for a boolean value. If only one
string is added to the projection, its value is taken to represent <code>true</code>. The absence of the string
represents <code>false</code>.</p>
<p>In the next example, the property <code>isCompany</code> will be shown as the keyword <code>COMPANY</code>. When the value
of the property is <code>true</code>, the keyword is shown. When the value is <code>false</code>, the keyword is not shown.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-default.edit#L67-L69


Entity &#123;[
    $&#123;self.isCompany [COMPANY]&#125; $&#123;self.name&#125;</code></pre>
<p>This example would be displayed as one of …</p>
<pre class="language-txt"><code class="language-txt">COMPANY entity PhilipsEnterPrises &#123; // the value of isCompany is true
  ...
&#125;
entity FritsPhilips &#123; // the value of isCompany is false
  ...
&#125;</code></pre>
<Note> 
{#snippet header()}Keywords defined within a projection overwrite the global boolean projection{/snippet}
{#snippet content()}
In the default editor you can define global boolean projection. These will not be used when a more specific projection
is present.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="switch-checkbox-radio-control-4"
  bind:intersecting={visibleById["switch-checkbox-radio-control-4"]}
  onIntersectChange={(visible) => setVisible("switch-checkbox-radio-control-4", visible)}
>Switch, Checkbox, Radio Control</SectionComponent>
<p>Freon provides four native boolean controls: two types of switches, a checkbox, and a radio group. Each is
indicated by adding a keyword to the property projection. The possible keywords are:</p>
<ul>
<li><code>switch</code>, which shows a simple switch control,</li>
<li><code>inner-switch</code>, which shows a switch that holds text for the true and false values,</li>
<li><code>checkbox</code>, which shows an ordinary checkbox,</li>
<li><code>radio</code>, which shows a radio group with text for the true and false values,</li>
<li><code>text</code>, which indicates that the boolean value should be shown as text.</li>
</ul>
<p>The latter is used to overwrite the global boolean projection, in case it demands one of the other options.
Whenever text is shown, the strings to be used as true and false values can be indicated between square brackets,
in the same way as the values for textual representations are given. When they are not present, the global
definition is taken. If that is also not present then the default is <code>true</code> and <code>false</code>.</p>
<p>For the sake of the example, we have added a number of boolean properties to the concept <code>BaseProduct</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L24-L39

concept BaseProduct &#123;
    name: identifier;               // internal name
    isUnderConstruction: boolean;   // defines whether this base product is still 'raw'
    theme: InsuranceTheme;          // the 'kind' of insurance
    parts: InsurancePart[];         // all parts of this product
    // The following properties are present to show the different options for displaying booleans.
    isApprovedLevel1: boolean;
    isApprovedLevel2: boolean;
    isApprovedLevel3: boolean;
    yieldsProfit: boolean;
    range: number;
    nrOfUse: number;
    // The previoud two properties are present to show the different options for displaying numbers.
    // The following property is present to show the use of an external DatePicker component.
    date: string;
&#125;</code></pre>
<p>Each of the boolean properties is displayed differently.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-controls.edit#L7-L11

is still under construction: $&#123;self.isUnderConstruction switch&#125;
is approved level1: $&#123;self.isApprovedLevel1 radio [Sure | NoWay]&#125;
is approved level2: $&#123;self.isApprovedLevel2 inner-switch&#125;
is approved level3: $&#123;self.isApprovedLevel3 checkbox&#125;
yields profit: $&#123;self.yieldsProfit text [Plenty | Little]&#125;</code></pre>
<p>The result of this projection is shown in the screenshot below. Note that
the text used for property <code>self.isApprovedLevel2</code> is defined by the global
boolean projection. See <a
  href="/Documentation/Defining_an_Editor/Global_Projections"
>Global Projections</a>.</p>
<Figure
imageName={'documentation/Documentation-Boolean-Projections-screenshot2.png'}
caption={'Examples of different boolean controls'}
figureNumber={2}
/>


<PrevNextSection {prevLink} {nextLink} />